// The character window (C): Equipment, Skills, Quests and Journal tabs, plus the small quest tracker on screen.
import { EQUIP_LABEL, EQUIP_SLOTS, itemDef, type InvSlot } from '../../../shared/items.js';
import { SKILLS, levelForXp, xpForLevel } from '../../../shared/skills.js';
import { JOBS, ABILITIES, STATUS, jobSkill, statsFor, JOB_MAX_LEVEL, type JobId } from '../../../shared/jobs.js';
import { QUESTS, goalNeed, questDef, type QuestView } from '../../../shared/quests.js';
import type { JournalEntry } from '../../../shared/protocol.js';
import type { Look } from '../../../shared/look.js';
import { itemIcon } from '../render/items.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; }
type Tab = 'equip' | 'jobs' | 'skills' | 'quests' | 'journal';
const DIRS = ['down', 'right', 'up', 'left'];

export class CharacterUI {
  name = ''; look: Look | null = null;
  equip = new Map<number, InvSlot>();
  xp: Record<string, number> = {};
  quests = new Map<string, QuestView>();
  journal: JournalEntry[] = []; notes = '';
  tracked: string | null = null;
  onUnequip: (slot: number) => void = () => {};
  onNotes: (text: string) => void = () => {};
  /** Jobs: which one you are, whether you can change here, and the request to change. */
  job: JobId = 'knight';
  canChangeJob: () => boolean = () => false;
  onJob: (id: JobId) => void = () => {};
  setJob(id: JobId): void { this.job = id; this.refresh('jobs'); }
  /** Draws your character into a canvas, facing a direction. */
  drawSprite: (canvas: HTMLCanvasElement, look: Look, dir: string) => void = () => {};
  private win = $('charwin'); private tab: Tab = 'equip'; private dir = 0; private notesTimer = 0;

  constructor() {
    $('charBtn').addEventListener('click', () => this.toggle());
    $('charClose').addEventListener('click', () => this.toggle(false));
    for (const b of this.win.querySelectorAll<HTMLButtonElement>('[data-ctab]')) b.addEventListener('click', () => { this.tab = b.dataset.ctab as Tab; this.render(); });
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if ((e.code === 'KeyH' || e.code === 'KeyL') && !e.repeat) {   // H: character, L: quest log
        e.preventDefault(); const tab: Tab = e.code === 'KeyL' ? 'quests' : 'equip';
        this.toggle(this.open && this.tab === tab ? false : true, tab);
      }
      if (e.code === 'Escape' && this.open) this.toggle(false);
    });
    $('tracker').addEventListener('click', () => this.toggle(true, 'quests'));
  }

  get open(): boolean { return !this.win.hidden; }
  toggle(force?: boolean, tab?: Tab): void { if (tab) this.tab = tab; this.win.hidden = !(force ?? this.win.hidden); if (this.open) this.render(); }
  reset(): void { this.equip.clear(); this.quests.clear(); this.journal = []; this.notes = ''; this.toggle(false); this.renderTracker(); }

  // ---------- updates from the server ----------
  setEquip(list: InvSlot[]): void { this.equip = new Map(list.map(s => [s.slot, s])); this.refresh('equip'); }
  applyEquip(eq: { set: InvSlot[]; clear: number[] }): void { for (const c of eq.clear) this.equip.delete(c); for (const s of eq.set) this.equip.set(s.slot, s); this.refresh('equip'); }
  setLook(look: Look): void { this.look = look; this.refresh('equip'); }
  setXp(xp: Record<string, number>): void { this.xp = { ...xp }; this.refresh('skills'); }
  addXp(skill: string, total: number): void { this.xp[skill] = total; this.refresh('skills'); this.refresh('jobs'); }
  setQuests(list: QuestView[]): void { this.quests = new Map(list.map(q => [q.id, q])); this.refresh('quests'); this.renderTracker(); }
  updateQuest(q: QuestView): void { this.quests.set(q.id, q); if (q.state === 'done' && this.tracked === q.id) this.tracked = null; this.refresh('quests'); this.renderTracker(); }
  setJournal(entries: JournalEntry[], notes: string): void { this.journal = entries; this.notes = notes; this.refresh('journal'); }
  addJournal(e: JournalEntry): void { this.journal.unshift(e); this.refresh('journal'); }

  private refresh(tab: Tab): void { if (this.open && this.tab === tab) this.render(); }

  // ---------- tabs ----------
  private render(): void {
    for (const b of this.win.querySelectorAll<HTMLButtonElement>('[data-ctab]')) b.setAttribute('aria-selected', String(b.dataset.ctab === this.tab));
    const box = $('charBody'); box.replaceChildren();
    $('charTitle').textContent = this.name || 'Character';
    if (this.tab === 'equip') this.renderEquip(box);
    else if (this.tab === 'jobs') this.renderJobs(box);
    else if (this.tab === 'skills') this.renderSkills(box);
    else if (this.tab === 'quests') this.renderQuests(box);
    else this.renderJournal(box);
  }

  /** Every job: its level and xp, stats at that level, what it knows and what's coming, and the change button. */
  private renderJobs(box: HTMLElement): void {
    const here = this.canChangeJob();
    box.append(el('p', 'jb-note', here ? 'You can change job here. Each job levels on its own.' : 'Change job in the Guild Hall (or at home). Each job levels on its own, from battles fought as that job.'));
    const list = el('div', 'jb-list');
    for (const j of JOBS) {
      const xp = this.xp[jobSkill(j.id)] ?? 0, lv = Math.min(JOB_MAX_LEVEL, levelForXp(xp)), lo = xpForLevel(lv), hi = xpForLevel(lv + 1), frac = hi > lo ? (xp - lo) / (hi - lo) : 1;
      const card = el('div', 'jb-card' + (j.id === this.job ? ' on' : ''));
      const head = el('div', 'jb-head'), icon = el('span', 'jb-icon', j.icon); icon.style.background = j.color;
      const names = el('div'); names.append(el('div', 'jb-name', j.name), el('div', 'jb-role', j.role));
      head.append(icon, names);
      if (j.id === this.job) head.append(el('span', 'jb-badge', 'Current'));
      head.append(el('span', 'jb-lv', `Lv ${lv}`));
      const bar = el('div', 'sk-bar'), fill = el('i'); fill.style.width = Math.round(frac * 100) + '%'; bar.append(fill); bar.title = `${xp - lo} / ${hi - lo} xp to level ${lv + 1}`;
      const st = statsFor(j.id, lv), stats = el('div', 'jb-stats');
      for (const [k, label] of [['hp', 'HP'], ['mp', 'MP'], ['str', 'STR'], ['mag', 'MAG'], ['def', 'DEF'], ['spd', 'SPD']] as const) { const s = el('span', '', label + ' '); s.append(el('b', '', String(st[k]))); stats.append(s); }
      const abs = el('div', 'jb-abs');
      for (const a of ABILITIES.filter(x => x.job === j.id)) {
        const chip = el('span', `jb-ab ${a.kind}${a.level > lv ? ' locked' : ''}`, a.level > lv ? `${a.name} · Lv ${a.level}` : a.name);
        chip.title = `${a.desc}${a.mp ? ` ${a.mp} MP.` : ''}${a.status ? ` (${STATUS[a.status.id].name})` : ''}`; abs.append(chip);
      }
      card.append(head, bar, el('p', 'jb-desc', j.desc), stats, abs);
      if (j.id !== this.job) {
        const b = el('button', 'btn' + (here ? ' primary' : ''), `Become a ${j.name}`); b.type = 'button'; b.disabled = !here;
        if (!here) b.title = 'Go to the Guild Hall (or home) to change job';
        b.addEventListener('click', () => this.onJob(j.id)); card.append(b);
      }
      list.append(card);
    }
    box.append(list);
  }

  private renderEquip(box: HTMLElement): void {
    const wrap = el('div', 'eq-wrap'), left = el('div', 'eq-col'), right = el('div', 'eq-col');
    const stage = el('div', 'eq-stage'), cv = el('canvas'); cv.width = 32; cv.height = 34;
    if (this.look) this.drawSprite(cv, this.look, DIRS[this.dir]);
    const turn = el('div', 'eq-turn');
    const b = (t: string, d: number) => { const x = el('button', '', t); x.type = 'button'; x.setAttribute('aria-label', d < 0 ? 'Turn left' : 'Turn right'); x.addEventListener('click', () => { this.dir = (this.dir + d + 4) % 4; this.render(); }); return x; };
    turn.append(b('◀', -1), b('▶', 1));
    stage.append(cv, turn);
    EQUIP_SLOTS.forEach((slot, i) => {
      const s = this.equip.get(i), cell = el('button', 'eq-slot' + (s ? ' full' : '')); cell.type = 'button'; cell.dataset.eq = String(i);
      const icon = el('span', 'eq-icon');
      if (s) { const c = itemIcon(s.def).cloneNode() as HTMLCanvasElement; c.getContext('2d')!.drawImage(itemIcon(s.def), 0, 0); icon.append(c); }
      const d = s ? itemDef(s.def) : null;
      const text = el('span', 'eq-text'); text.append(el('small', '', EQUIP_LABEL[slot]), el('b', '', d?.name ?? 'Empty'));
      cell.append(icon, text);
      cell.title = d ? `${d.name}: ${d.desc}${s?.bound ? ' (bound to you)' : ''}. Click to take off.` : `${EQUIP_LABEL[slot]}: drag gear here from your bag`;
      if (s) cell.addEventListener('click', () => this.onUnequip(i));
      (i % 2 ? right : left).append(cell);
    });
    wrap.append(left, stage, right);
    const touch = matchMedia('(pointer:coarse)').matches;
    box.append(wrap, el('p', 'eq-hint', (touch ? 'Tap gear in your bag, then Wear. Tap worn gear to take it off.' : 'Drag gear from your bag onto this window, or double-click it in the bag. Click worn gear to take it off.') + ' What you wear is what everyone sees.'));
  }

  private renderSkills(box: HTMLElement): void {
    const kinds: [string, string][] = [['gathering', 'Gathering'], ['crafting', 'Crafting']];
    for (const [k, label] of kinds) {
      box.append(el('h3', 'set-h', label));
      for (const sk of SKILLS.filter(s => s.kind === k)) {
        const xp = this.xp[sk.id] ?? 0, lv = levelForXp(xp), lo = xpForLevel(lv), hi = xpForLevel(lv + 1), frac = hi > lo ? (xp - lo) / (hi - lo) : 1;
        const row = el('div', 'sk-row' + (xp ? '' : ' none'));
        const bar = el('div', 'sk-bar'), fill = el('i'); fill.style.width = Math.round(frac * 100) + '%'; bar.append(fill);
        row.append(el('span', 'sk-name', sk.name), el('b', 'sk-lv', String(lv)), bar, el('small', 'sk-xp', xp ? `${xp - lo} / ${hi - lo}` : 'Not started'));
        box.append(row);
      }
    }
    box.append(el('p', 'eq-hint', 'Skills rise only by doing them. Jobs arrive with combat, each leveling on its own.'));
  }

  private renderQuests(box: HTMLElement): void {
    const active = QUESTS.filter(q => this.quests.get(q.id)?.state === 'active'), done = QUESTS.filter(q => this.quests.get(q.id)?.state === 'done');
    if (!active.length) box.append(el('p', 'empty', done.length ? 'All caught up! New quests arrive as the town grows.' : 'No quests yet.'));
    for (const def of active) {
      const v = this.quests.get(def.id)!, card = el('div', 'qc');
      const head = el('div', 'qc-head'); head.append(el('b', '', def.name), el('small', '', def.giver));
      const track = el('button', 'pf-mini', this.trackedId() === def.id ? 'Tracking' : 'Track'); track.type = 'button';
      track.addEventListener('click', () => { this.tracked = def.id; this.render(); this.renderTracker(); });
      head.append(track);
      const goals = el('ul', 'qc-goals');
      def.goals.forEach((g, i) => { const n = Math.min(v.progress[i] ?? 0, goalNeed(g)), need = goalNeed(g), li = el('li', n >= need ? 'done' : '', g.text + (need > 1 ? ` (${n}/${need})` : '')); goals.append(li); });
      const rw = el('div', 'qc-reward', 'Reward: ' + [...(def.reward.items ?? []).map(i => `${i.qty > 1 ? i.qty + ' ' : ''}${itemDef(i.def)?.name}`), ...(def.reward.gold ? [`${def.reward.gold} gold`] : [])].join(', '));
      card.append(head, el('p', 'qc-story', '“' + def.story + '”'), goals, rw);
      box.append(card);
    }
    if (done.length) {
      const d = el('details', 'qc-done'); d.append(el('summary', '', `Completed (${done.length})`));
      for (const def of done) d.append(el('div', '', `✓ ${def.name}, for ${def.giver}`));
      box.append(d);
    }
  }

  private renderJournal(box: HTMLElement): void {
    const notes = el('textarea', 'jn-notes'); notes.placeholder = 'Your own notes: plans, friends, where the best mushrooms are…'; notes.value = this.notes; notes.maxLength = 4000;
    notes.addEventListener('input', () => { this.notes = notes.value; clearTimeout(this.notesTimer); this.notesTimer = window.setTimeout(() => this.onNotes(this.notes), 800); });
    notes.addEventListener('blur', () => { clearTimeout(this.notesTimer); this.onNotes(this.notes); });
    box.append(el('h3', 'set-h', 'Notes'), notes, el('h3', 'set-h', 'Your story so far'));
    if (!this.journal.length) box.append(el('p', 'empty', 'Explore, forage and meet people. The firsts get written here.'));
    const ICON: Record<string, string> = { place: '⌂', item: '✿', quest: '★', level: '▲', party: '♥', photo: '◉' };
    const list = el('ol', 'jn-list');
    for (const e of this.journal) {
      const li = el('li', 'jn-' + e.kind); li.append(el('span', 'jn-icon', ICON[e.kind] ?? '•'), el('span', '', e.text), el('small', '', when(e.at)));
      list.append(li);
    }
    box.append(list);
  }

  // ---------- the tracker ----------
  private trackedId(): string | null {
    if (this.tracked && this.quests.get(this.tracked)?.state === 'active') return this.tracked;
    return QUESTS.find(q => this.quests.get(q.id)?.state === 'active')?.id ?? null;
  }
  renderTracker(): void {
    const t = $('tracker'), id = this.trackedId(), def = id ? questDef(id) : null, v = id ? this.quests.get(id) : null;
    t.hidden = !def || !v; t.replaceChildren(); if (!def || !v) return;
    t.append(el('b', '', def.name));
    def.goals.forEach((g, i) => { const n = Math.min(v.progress[i] ?? 0, goalNeed(g)), need = goalNeed(g); t.append(el('div', n >= need ? 'done' : '', `${n >= need ? '✓' : '·'} ${g.text}${need > 1 ? ` ${n}/${need}` : ''}`)); });
  }
}

function when(at: number): string {
  const s = (Date.now() - at) / 1000;
  if (s < 60) return 'just now'; if (s < 3600) return `${Math.floor(s / 60)} min ago`; if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(at).toLocaleDateString();
}
